import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, Lock, Smartphone, Key, Eye, EyeOff, Copy, CheckCircle2, AlertTriangle, Clock, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettings() {
  const [settings, setSettings] = useState(null);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPw, setChangingPw] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, mfaRes] = await Promise.all([
        axios.get(`${API_URL}/api/security/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/security/mfa/status`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setMfaStatus(mfaRes.data);
    } catch { toast.error('Failed to load security settings'); }
    setLoading(false);
  };

  const startMfaSetup = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/security/mfa/setup`, {}, { withCredentials: true });
      setMfaSetupData(data);
      setShowMfaSetup(true);
    } catch { toast.error('Failed to start MFA setup'); }
  };

  const verifyMfa = async () => {
    if (verifyCode.length < 6) { toast.error('Enter a 6-digit code'); return; }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/security/mfa/verify`, { code: verifyCode }, { withCredentials: true });
      toast.success('MFA enabled successfully');
      setShowMfaSetup(false);
      setVerifyCode('');
      setMfaSetupData(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    setVerifying(false);
  };

  const disableMfa = async () => {
    if (!disableCode) { toast.error('Enter your MFA or backup code'); return; }
    try {
      await axios.post(`${API_URL}/api/security/mfa/disable`, { code: disableCode }, { withCredentials: true });
      toast.success('MFA disabled');
      setShowMfaDisable(false);
      setDisableCode('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API_URL}/api/security/change-password`, {
        current_password: pwForm.current_password, new_password: pwForm.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    setChangingPw(false);
  };

  const copyBackupCodes = () => {
    if (mfaSetupData?.backup_codes) {
      navigator.clipboard.writeText(mfaSetupData.backup_codes.join('\n'));
      setCopiedCodes(true);
      toast.success('Backup codes copied');
      setTimeout(() => setCopiedCodes(false), 2000);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="security-settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Security Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account security and authentication</p>
        </div>

        {/* MFA Section */}
        <Card className="glass-card border-border" data-testid="mfa-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-[#00A6A0]/10">
                <Smartphone className="w-5 h-5 text-[#00A6A0]" />
              </div>
              <div>
                <CardTitle className="text-foreground">Two-Factor Authentication</CardTitle>
                <CardDescription>Add an extra layer of security with an authenticator app</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${mfaStatus?.mfa_enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {mfaStatus?.mfa_enabled ? 'Enabled' : 'Not enabled'}
                  </p>
                  {mfaStatus?.enabled_at && (
                    <p className="text-xs text-muted-foreground">Since {new Date(mfaStatus.enabled_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
              {mfaStatus?.mfa_enabled ? (
                <Button variant="outline" className="border-red-500/30 text-red-500 hover:bg-red-500/10" onClick={() => setShowMfaDisable(true)} data-testid="disable-mfa-btn">
                  Disable MFA
                </Button>
              ) : (
                <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={startMfaSetup} data-testid="enable-mfa-btn">
                  Enable MFA
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card className="glass-card border-border" data-testid="password-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-500/10">
                <Lock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <CardTitle className="text-foreground">Password</CardTitle>
                <CardDescription>
                  {settings?.last_password_change
                    ? `Last changed ${new Date(settings.last_password_change).toLocaleDateString()}`
                    : 'Manage your password'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="border-border text-foreground" onClick={() => setShowPasswordChange(true)} data-testid="change-password-btn">
              Change Password
            </Button>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card className="glass-card border-border" data-testid="sessions-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-500/10">
                <Monitor className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle className="text-foreground">Active Sessions</CardTitle>
                <CardDescription>{settings?.active_sessions || 0} active session(s)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {settings?.login_history?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground mb-3">Recent Login Activity</p>
                {settings.login_history.map((entry, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{entry.ip || 'Unknown IP'}</p>
                      <p className="text-xs text-muted-foreground">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent login history available</p>
            )}
          </CardContent>
        </Card>

        {/* MFA Setup Dialog */}
        <Dialog open={showMfaSetup} onOpenChange={setShowMfaSetup}>
          <DialogContent className="sm:max-w-lg glass-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Set Up Two-Factor Authentication</DialogTitle>
            </DialogHeader>
            {mfaSetupData && (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
                  <div className="inline-block p-4 bg-white rounded-xl">
                    <img src={mfaSetupData.qr_code} alt="MFA QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground text-xs">Manual Key</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={mfaSetupData.secret} readOnly className="font-mono text-xs bg-muted border-border text-foreground" data-testid="mfa-secret-key" />
                    <Button variant="outline" size="sm" className="border-border" onClick={() => { navigator.clipboard.writeText(mfaSetupData.secret); toast.success('Copied'); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-foreground text-xs">Backup Codes (save these!)</Label>
                    <Button variant="ghost" size="sm" className="text-muted-foreground h-7" onClick={copyBackupCodes}>
                      {copiedCodes ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 p-3 rounded-lg bg-muted/50 border border-border">
                    {mfaSetupData.backup_codes.map((code, i) => (
                      <span key={i} className="text-xs font-mono text-foreground">{code}</span>
                    ))}
                  </div>
                  <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Store these codes safely. They can be used if you lose your device.
                  </p>
                </div>
                <div>
                  <Label className="text-foreground">Verification Code</Label>
                  <Input
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="mt-1 bg-muted border-border text-foreground font-mono text-center text-lg tracking-widest"
                    data-testid="mfa-verify-input"
                  />
                </div>
                <Button className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={verifyMfa} disabled={verifying || verifyCode.length < 6} data-testid="mfa-verify-btn">
                  {verifying ? 'Verifying...' : 'Verify & Enable MFA'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* MFA Disable Dialog */}
        <Dialog open={showMfaDisable} onOpenChange={setShowMfaDisable}>
          <DialogContent className="sm:max-w-md glass-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Disable Two-Factor Authentication</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter your current MFA code or a backup code to disable two-factor authentication.</p>
              <div>
                <Label className="text-foreground">Code</Label>
                <Input
                  value={disableCode}
                  onChange={e => setDisableCode(e.target.value)}
                  placeholder="MFA code or backup code"
                  className="mt-1 bg-muted border-border text-foreground font-mono"
                  data-testid="mfa-disable-input"
                />
              </div>
              <Button className="w-full bg-red-500 hover:bg-red-600 text-white" onClick={disableMfa} data-testid="mfa-disable-confirm-btn">
                Disable MFA
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
          <DialogContent className="sm:max-w-md glass-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Change Password</DialogTitle>
            </DialogHeader>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <Label className="text-foreground">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    value={pwForm.current_password}
                    onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                    className="bg-muted border-border text-foreground pr-10"
                    data-testid="current-password-input"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-foreground">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={pwForm.new_password}
                    onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                    className="bg-muted border-border text-foreground pr-10"
                    data-testid="new-password-input"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-foreground">Confirm New Password</Label>
                <Input
                  type="password"
                  value={pwForm.confirm_password}
                  onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                  className="mt-1 bg-muted border-border text-foreground"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={changingPw} data-testid="change-password-submit-btn">
                {changingPw ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
